import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCabLandingPageSettings, updateCabLandingPageSettings } from '../../../api/cabsAdmin';
import { uploadAdminAsset } from '../../../api/cabsHomepageAdmin';

const defaultVisibility = {
  hero: true,
  vehicles: true,
  guestStories: true,
  travelGuides: true,
  videos: true,
  popularPackages: true,
  popularRoutes: true,
  footerCta: true,
};

const defaultForm = {
  heroEyebrow: 'Oxitaxi Cabs',
  heroTitle: '',
  heroSubtitle: '',
  heroBackgroundImageUrl: '',
  heroPrimaryButtonText: 'Search rides',
  heroPrimaryButtonLink: '/cabs/list',
  vehicleSectionTitle: 'Vehicles for every journey',
  vehicleSectionSubtitle: '',
  guestStoriesTitle: 'Guest trip highlights',
  guestStoriesSubtitle: '',
  travelGuidesTitle: 'Travel guides & tips',
  travelGuidesSubtitle: '',
  videosTitle: 'Promotional videos',
  videosSubtitle: '',
  popularPackagesTitle: 'Popular packages',
  popularPackagesSubtitle: '',
  popularRoutesTitle: 'Popular routes',
  popularRoutesSubtitle: '',
  footerCtaTitle: 'Ready to plan your next ride?',
  footerCtaSubtitle: '',
  footerCtaButtonText: 'Request a quote',
  footerCtaButtonLink: '/cabs/list',
  sectionVisibility: defaultVisibility,
  sectionOrder: ['hero', 'vehicles', 'guestStories', 'travelGuides', 'videos', 'popularPackages', 'popularRoutes', 'footerCta'],
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
};

const sectionLinks = [
  { label: 'Hero banners', to: '/admin/cabs/hero-banners' },
  { label: 'Vehicle categories', to: '/admin/cabs/vehicle-categories' },
  { label: 'Guest trips', to: '/admin/cabs/guest-trips' },
  { label: 'Blogs', to: '/admin/cabs/blogs' },
  { label: 'Promo videos', to: '/admin/cabs/promo-videos' },
  { label: 'Popular routes', to: '/admin/cabs/popular-routes' },
  { label: 'Transport packages', to: '/admin/cabs/transport-packages' },
  { label: 'Footer', to: '/admin/cabs/footer' },
];

const sectionLabels = {
  hero: 'Hero',
  vehicles: 'Vehicle categories',
  guestStories: 'Guest stories',
  travelGuides: 'Travel guides',
  videos: 'Promo videos',
  popularPackages: 'Popular packages',
  popularRoutes: 'Popular routes',
  footerCta: 'Footer CTA',
};

const AdminCabsCms = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['cab-landing-settings'],
    queryFn: getCabLandingPageSettings,
  });
  const [form, setForm] = useState(defaultForm);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const settings = data?.data || data;
    if (!settings) return;
    setForm({
      ...defaultForm,
      ...settings,
      sectionVisibility: { ...defaultVisibility, ...(settings.sectionVisibility || {}) },
      sectionOrder: Array.isArray(settings.sectionOrder) && settings.sectionOrder.length
        ? settings.sectionOrder
        : defaultForm.sectionOrder,
    });
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload) => updateCabLandingPageSettings(payload),
    onSuccess: () => setMessage('Landing page settings updated.'),
    onError: (error) => setMessage(error.message || 'Failed to update landing page settings.'),
  });

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const setVisibility = (key, value) => {
    setForm((current) => ({
      ...current,
      sectionVisibility: {
        ...current.sectionVisibility,
        [key]: value,
      },
    }));
  };

  const uploadHeroImage = async (file) => {
    if (!file) return;
    setUploading(true);
    setMessage('');
    try {
      const uploaded = await uploadAdminAsset(file);
      const url = uploaded?.path || uploaded?.url || uploaded?.data?.path || '';
      if (url) setField('heroBackgroundImageUrl', url);
    } catch (error) {
      setMessage(error.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const save = () => {
    mutation.mutate(form);
  };

  return (
    <div className="space-y-4">
      <div className="d-flex flex-wrap justify-content-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600">Cab Services</p>
          <h1 className="text-2xl font-semibold text-slate-900">Landing Page Builder</h1>
          <p className="text-sm text-slate-600">Control Oxitaxi hero copy, section headings, visibility, CTA copy, and SEO.</p>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={mutation.isPending || isLoading}>
          {mutation.isPending ? 'Saving...' : 'Save settings'}
        </button>
      </div>

      {message && <div className="alert alert-info">{message}</div>}

      <div className="grid gap-4 xl:grid-cols-[1.4fr_0.6fr]">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-slate-900">Hero</h2>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Eyebrow</label>
                <input className="form-control" value={form.heroEyebrow} onChange={(e) => setField('heroEyebrow', e.target.value)} />
              </div>
              <div className="col-md-8">
                <label className="form-label">Hero title</label>
                <input className="form-control" value={form.heroTitle} onChange={(e) => setField('heroTitle', e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">Hero subtitle</label>
                <textarea className="form-control" rows={3} value={form.heroSubtitle} onChange={(e) => setField('heroSubtitle', e.target.value)} />
              </div>
              <div className="col-md-8">
                <label className="form-label">Background image URL</label>
                <input className="form-control" value={form.heroBackgroundImageUrl} onChange={(e) => setField('heroBackgroundImageUrl', e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Upload image</label>
                <input className="form-control" type="file" accept="image/*" disabled={uploading} onChange={(e) => uploadHeroImage(e.target.files?.[0])} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Primary button text</label>
                <input className="form-control" value={form.heroPrimaryButtonText} onChange={(e) => setField('heroPrimaryButtonText', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Primary button link</label>
                <input className="form-control" value={form.heroPrimaryButtonLink} onChange={(e) => setField('heroPrimaryButtonLink', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-slate-900">Section Headings</h2>
            <div className="row g-3">
              {[
                ['vehicleSectionTitle', 'Vehicle section title'],
                ['vehicleSectionSubtitle', 'Vehicle section subtitle'],
                ['guestStoriesTitle', 'Guest stories title'],
                ['guestStoriesSubtitle', 'Guest stories subtitle'],
                ['travelGuidesTitle', 'Travel guides title'],
                ['travelGuidesSubtitle', 'Travel guides subtitle'],
                ['videosTitle', 'Videos title'],
                ['videosSubtitle', 'Videos subtitle'],
                ['popularPackagesTitle', 'Popular packages title'],
                ['popularPackagesSubtitle', 'Popular packages subtitle'],
                ['popularRoutesTitle', 'Popular routes title'],
                ['popularRoutesSubtitle', 'Popular routes subtitle'],
                ['footerCtaTitle', 'Footer CTA title'],
                ['footerCtaSubtitle', 'Footer CTA subtitle'],
                ['footerCtaButtonText', 'Footer CTA button text'],
                ['footerCtaButtonLink', 'Footer CTA button link'],
              ].map(([key, label]) => (
                <div className="col-md-6" key={key}>
                  <label className="form-label">{label}</label>
                  <input className="form-control" value={form[key] || ''} onChange={(e) => setField(key, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold text-slate-900">SEO</h2>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">SEO title</label>
                <input className="form-control" value={form.seoTitle || ''} onChange={(e) => setField('seoTitle', e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">SEO keywords</label>
                <input className="form-control" value={form.seoKeywords || ''} onChange={(e) => setField('seoKeywords', e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">SEO description</label>
                <textarea className="form-control" rows={3} value={form.seoDescription || ''} onChange={(e) => setField('seoDescription', e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Visibility</h2>
            <div className="mt-3 space-y-2">
              {Object.entries(sectionLabels).map(([key, label]) => (
                <label className="d-flex align-items-center justify-content-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" key={key}>
                  <span className="text-sm text-slate-700">{label}</span>
                  <input
                    type="checkbox"
                    checked={Boolean(form.sectionVisibility?.[key])}
                    onChange={(e) => setVisibility(key, e.target.checked)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Content Sections</h2>
            <div className="mt-3 d-flex flex-column gap-2">
              {sectionLinks.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 no-underline hover:bg-slate-50"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">Preview Notes</h2>
            <p className="mb-2 text-sm text-slate-600">
              The public Oxitaxi landing page now reads these settings through the cab landing API. Hero banners,
              guest stories, guides, promo videos, routes, packages, and footer content remain fully dynamic from their
              own admin sections.
            </p>
            <div className="text-xs text-slate-500">
              Save this page, then refresh <code>/cabs</code> to review the updated landing copy and section visibility.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCabsCms;
